import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Print as PrintIcon,
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColumnVisibilityModel } from '@mui/x-data-grid';
import type { GridSortModel } from '@mui/x-data-grid';
import { useIESummary, useEntities } from '@/hooks';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { createIeColumns } from '@/components/iem/shared/columns';
import { TableSkeleton } from '@/components/iem/tables/TableSkeleton';
import { useNotifications } from '@/stores/useNotificationStore';
import { useAuth } from '@/hooks/useAuth';
import { useFrontWindowIePreferences } from '@/hooks/useFrontWindowPreferences';
import { EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { runIeSummaryReport } from '@/reports/runners/ieSummaryRunner';
import type { IeSummaryMemberInfo } from '@/reports/templates/IeSummaryTemplate';
import {
  useColumnPreferencesStore,
  type ColumnVisibilityModel,
} from '@/stores/useColumnPreferencesStore';
import type { IeView } from '@/stores/frontWindowPreferencesStore';
import type { IEItem } from '@/services/iem';
import { useTabActions } from '@/components/iem/panels/TabActionsContext';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { usePersonLabels } from '@/hooks/usePersonLabels';

type IeTableProps = {
  onClearError: () => void;
};

const FRONT_WINDOW_IE_PREF_KEY = 'front-window-ieItem-columns';
const MANAGEABLE_COLUMNS = [
  { field: 'lin', label: 'LIN' },
  { field: 'nsn', label: 'NSN' },
  { field: 'kitName', label: 'IE Kit' }, // overridden at runtime
  { field: 'unitPrice', label: 'Unit Price' },
  { field: 'status', label: 'Status' },
  { field: 'authorizedQty', label: 'Qty Authorized' },
  { field: 'currentQty', label: 'Qty Issued' },
] as const;

type ManageableColumnField = (typeof MANAGEABLE_COLUMNS)[number]['field'];

const buildVisibilityModel = (model?: ColumnVisibilityModel): GridColumnVisibilityModel => {
  const next: GridColumnVisibilityModel = {};
  MANAGEABLE_COLUMNS.forEach(({ field }) => {
    next[field] = model?.[field] !== false;
  });
  return next;
};

const visibilityModelsEqual = (a: GridColumnVisibilityModel, b: GridColumnVisibilityModel) =>
  MANAGEABLE_COLUMNS.every(({ field }) => a[field] === b[field]);

const isOverage = (item: IEItem): boolean => item.authorizedQty > 0 && item.currentQty > item.authorizedQty;
const isShortage = (item: IEItem): boolean => item.currentQty < item.authorizedQty;
const isUnauthorized = (item: IEItem): boolean => item.authorizedQty === 0 && item.currentQty > 0;

export const IeTable: React.FC<IeTableProps> = ({ onClearError }) => {
  const { currentCustomer } = useIemFrontDoorStore();
  const { user } = useAuth();
  const tabActions = useTabActions();
  const { columnLabel: ieKitLabel } = useIeKitLabels('front_window');
  const { preferences: iePrefs, updatePreferences: updateIePrefs } = useFrontWindowIePreferences(
    user?.id,
  );
  const personId = currentCustomer?.id || '';
  const view: IeView = iePrefs.view ?? 'shortages';
  const search = iePrefs.search ?? '';
  const {
    data: ieData,
    isLoading,
    error,
    isFetched,
  } = useIESummary(personId, EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL);
  const { error: notifyError, warning: notifyWarning } = useNotifications();
  const person = usePersonLabels('front_window');
  const [hideError, setHideError] = useState(false);
  const [columnSelectorAnchor, setColumnSelectorAnchor] = useState<HTMLElement | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printingRef = useRef(false);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const effectiveUserId = user?.id ?? 'anonymous';

  const sortModel = iePrefs.sortModel;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => {
      const stored = useColumnPreferencesStore
        .getState()
        .getVisibility(effectiveUserId, FRONT_WINDOW_IE_PREF_KEY);
      return buildVisibilityModel(stored);
    },
  );

  useEffect(() => {
    const stored = useColumnPreferencesStore
      .getState()
      .getVisibility(effectiveUserId, FRONT_WINDOW_IE_PREF_KEY);
    setColumnVisibilityModel(buildVisibilityModel(stored));
  }, [effectiveUserId]);

  useEffect(() => {
    updateIePrefs({ columnVisibility: columnVisibilityModel });
  }, [columnVisibilityModel, updateIePrefs]);

  useEffect(() => {
    if (error) {
      notifyError(`${ieKitLabel} Load Failed`, error instanceof Error ? error.message : 'Unknown error');
      setHideError(false);
    }
  }, [error, notifyError, ieKitLabel]);

  const normalizedSearch = useMemo(() => search.trim().toLowerCase(), [search]);

  const ieCatalogIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of ieData ?? []) {
      if (item.catalogId) {
        ids.add(item.catalogId);
      }
    }
    return Array.from(ids).sort();
  }, [ieData]);

  const ieCatalogIdFilter = useMemo(() => {
    if (ieCatalogIds.length === 0) return null;
    if (ieCatalogIds.length === 1) return `eq.${ieCatalogIds[0]}`;
    return `in.(${ieCatalogIds.join(',')})`;
  }, [ieCatalogIds]);

  const { data: ieCatalogs = [] } = useEntities({
    type: 'catalog',
    limit: Math.max(ieCatalogIds.length, 100),
    filters: ieCatalogIdFilter ? { id: ieCatalogIdFilter } : {},
    enabled: Boolean(ieCatalogIdFilter),
  });

  const parentCatalogById = useMemo(() => {
    const map = new Map<string, string>();
    for (const catalog of ieCatalogs) {
      const parentId = catalog.entity_data?.parent_id;
      if (typeof parentId === 'string' && parentId.trim()) {
        map.set(catalog.id, parentId);
      }
    }
    return map;
  }, [ieCatalogs]);

  // Aggregate duplicate IE Kit rows by authorization family so sized and non-sized
  // variants are treated as the same item for shortage/overage/unauthorized tabs.
  const deduplicatedIeData = useMemo((): IEItem[] => {
    const groups = new Map<string, { item: IEItem; kitNames: Set<string> }>();
    for (const item of ieData ?? []) {
      const parentCatalogId = item.catalogId ? parentCatalogById.get(item.catalogId) ?? null : null;
      const familyCatalogId = parentCatalogId ?? item.catalogId ?? null;
      const normalizedLin = (item.lin ?? '').trim().toLowerCase();
      const normalizedNsn = (item.nsn ?? '').trim().toLowerCase();

      const key = familyCatalogId
        ? `family:${familyCatalogId}`
        : normalizedLin
          ? `lin:${normalizedLin}`
          : `nsn:${normalizedNsn}`;
      const kitName = (typeof item.kitName === 'string' && item.kitName.trim()) || null;
      const existing = groups.get(key);
      if (existing) {
        existing.item = {
          ...existing.item,
          authorizedQty: existing.item.authorizedQty + (item.authorizedQty ?? 0),
          currentQty: Math.max(existing.item.currentQty, item.currentQty ?? 0),
          availableQty: Math.max(existing.item.availableQty, item.availableQty ?? 0),
        };
        if (kitName) existing.kitNames.add(kitName);
      } else {
        groups.set(key, {
          item: { ...item },
          kitNames: kitName ? new Set([kitName]) : new Set(),
        });
      }
    }
    return Array.from(groups.values()).map(({ item, kitNames }) => ({
      ...item,
      kitName: kitNames.size > 0 ? Array.from(kitNames).sort().join(', ') : item.kitName,
    }));
  }, [ieData, parentCatalogById]);

  const viewFilteredData = useMemo(() => {
    if (view === 'shortages') {
      return deduplicatedIeData.filter(isShortage);
    }
    if (view === 'overages') {
      return deduplicatedIeData.filter(isOverage);
    }
    if (view === 'unauthorized') {
      return deduplicatedIeData.filter(isUnauthorized);
    }
    return deduplicatedIeData;
  }, [deduplicatedIeData, view]);

  const filteredData = useMemo(() => {
    if (!normalizedSearch) {
      return viewFilteredData;
    }
    return viewFilteredData.filter((item) => {
      const searchable = `${item.nsn ?? ''} ${item.item ?? ''} ${item.lin ?? ''} ${item.kitName ?? ''}`;
      return multiWordMatch(searchable, normalizedSearch);
    });
  }, [normalizedSearch, viewFilteredData]);

  // Sticky column headers: translate the header row when it scrolls past the viewport top
  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper) return;

    let scroller: HTMLElement | null = wrapper.parentElement;
    while (scroller && getComputedStyle(scroller).overflowY !== 'auto' && getComputedStyle(scroller).overflowY !== 'scroll') {
      scroller = scroller.parentElement;
    }
    if (!scroller) return;

    const handleScroll = () => {
      const topContainer = wrapper.querySelector<HTMLElement>('.MuiDataGrid-topContainer');
      if (!topContainer) return;

      const scrollerRect = scroller!.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const headerHeight = topContainer.offsetHeight;

      if (wrapperRect.top < scrollerRect.top && wrapperRect.bottom > scrollerRect.top + headerHeight) {
        const offset = scrollerRect.top - wrapperRect.top;
        topContainer.style.transform = `translateY(${offset}px)`;
        topContainer.style.position = 'relative';
        topContainer.style.zIndex = '3';
      } else {
        topContainer.style.transform = '';
        topContainer.style.position = '';
        topContainer.style.zIndex = '';
      }
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => scroller!.removeEventListener('scroll', handleScroll);
  }, [filteredData.length]);

  const catalogUnitPriceById = useMemo(() => {
    const map = new Map<string, number>();
    for (const catalog of ieCatalogs) {
      const raw = catalog.entity_data?.unit_price;
      const unitPrice =
        typeof raw === 'number' ? raw : typeof raw === 'string' ? Number.parseFloat(raw) : null;

      if (typeof unitPrice === 'number' && Number.isFinite(unitPrice)) {
        map.set(catalog.id, unitPrice);
      }
    }
    return map;
  }, [ieCatalogs]);

  const safeData = viewFilteredData;
  const rowsWithId = useMemo(() => {
    const sanitize = (v?: string | null) =>
      (v ?? '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') || 'x';
    return filteredData.map((item) => {
      const id = item.catalogId
        ? `ieItem-cat-${item.catalogId.replace(/[^a-z0-9]/gi, '')}`
        : `ieItem-${sanitize(item.lin)}-${sanitize(item.nsn)}`;
      const unitPrice = item.catalogId
        ? (catalogUnitPriceById.get(item.catalogId) ?? null)
        : null;
      return { ...item, id, unitPrice };
    });
  }, [catalogUnitPriceById, filteredData]);

  const ieColumns = useMemo(() => createIeColumns(ieKitLabel), [ieKitLabel]);

  const selectorOpen = Boolean(columnSelectorAnchor);
  const selectorId = selectorOpen ? 'front-window-ieItem-column-selector' : undefined;

  const handleViewChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, next: IeView | null) => {
      if (next && next !== view) {
        updateIePrefs({ view: next });
      }
    },
    [updateIePrefs, view],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateIePrefs({ search: event.target.value });
    },
    [updateIePrefs],
  );

  const handleToggleColumn = useCallback(
    (field: ManageableColumnField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = useColumnPreferencesStore
        .getState()
        .updateColumn(effectiveUserId, FRONT_WINDOW_IE_PREF_KEY, field, event.target.checked);
      const sanitized = buildVisibilityModel(next);
      setColumnVisibilityModel(sanitized);
      updateIePrefs({ columnVisibility: sanitized });
    },
    [effectiveUserId, updateIePrefs],
  );

  const handleVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const sanitized = buildVisibilityModel(model as ColumnVisibilityModel);
      setColumnVisibilityModel((prev) => {
        if (visibilityModelsEqual(prev, sanitized)) {
          return prev;
        }
        const sanitizedForStore = MANAGEABLE_COLUMNS.reduce<ColumnVisibilityModel>(
          (acc, column) => {
            acc[column.field] = sanitized[column.field] ?? true;
            return acc;
          },
          {} as ColumnVisibilityModel,
        );
        useColumnPreferencesStore
          .getState()
          .setVisibility(effectiveUserId, FRONT_WINDOW_IE_PREF_KEY, sanitizedForStore);
        return sanitized;
      });
      updateIePrefs({ columnVisibility: sanitized });
    },
    [effectiveUserId, updateIePrefs],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      updateIePrefs({ sortModel: model });
    },
    [updateIePrefs],
  );

  const paginationModel = useMemo(
    () => ({ pageSize: iePrefs.pageSize, page: 0 }),
    [iePrefs.pageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: { pageSize: number; page: number }) => {
      updateIePrefs({ pageSize: model.pageSize });
    },
    [updateIePrefs],
  );

  const handlePrintSummary = useCallback(async () => {
    if (printingRef.current) return;

    if (!currentCustomer) {
      notifyError(`Select ${person.article} ${person.lower}`, `Choose ${person.article} ${person.lower} before printing a summary.`);
      return;
    }

    const hasIeItems = (viewFilteredData.length) > 0;
    if (!hasIeItems) {
      notifyWarning('Nothing to print', `No ${ieKitLabel} items are available to print.`);
      return;
    }

    printingRef.current = true;
    setIsPrinting(true);

    const memberInfo: IeSummaryMemberInfo = {
      // Build name from first/last to avoid duplicating rank already embedded in display_name
      name:
        [currentCustomer.first_name, currentCustomer.last_name].filter(Boolean).join(' ').trim() ||
        currentCustomer.display_name,
      rank: currentCustomer.rank,
      unit: currentCustomer.unit,
      individualId: currentCustomer.individual_id,
    };

    const headingByView: Record<IeView, string> = {
      shortages: `${ieKitLabel} Summary \u2014 Shortages`,
      overages: `${ieKitLabel} Summary \u2014 Overages`,
      unauthorized: `${ieKitLabel} Summary \u2014 Unauthorized Items`,
      all: `${ieKitLabel} Summary`,
    };

    const filtersDescByView: Record<IeView, string> = {
      shortages: 'Filtered to shortages only.',
      overages: 'Filtered to overages only.',
      unauthorized: `Filtered to unauthorized items only (not on any assigned ${ieKitLabel}).`,
      all: `All ${ieKitLabel} items included.`,
    };

    try {
      await runIeSummaryReport({
        ieItems: viewFilteredData,
        member: memberInfo,
        heading: headingByView[view],
        filtersSummary: filtersDescByView[view],
        ieKitLabel,
      });
    } catch (err) {
      notifyError(
        'Print failed',
        err instanceof Error ? err.message : `Unable to generate ${ieKitLabel} summary.`,
      );
    } finally {
      setTimeout(() => {
        printingRef.current = false;
        setIsPrinting(false);
      }, 500);
    }
  }, [view, viewFilteredData, currentCustomer, notifyError, notifyWarning, ieKitLabel, person]);

  const printDisabled = isPrinting || isLoading;
  const actionButtons = useMemo(() => {
    if (!currentCustomer) return null;
    return (
      <Button
        size="small"
        variant="contained"
        startIcon={<PrintIcon fontSize="small" />}
        onClick={() => handlePrintSummary()}
        disabled={printDisabled}
      >
        Print Summary
      </Button>
    );
  }, [currentCustomer, handlePrintSummary, printDisabled]);

  useEffect(() => {
    if (!tabActions) return;
    tabActions.setActions(actionButtons);
    return () => tabActions.setActions(null);
  }, [actionButtons, tabActions]);

  return (
    <>
      {error && !hideError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          onClose={() => {
            setHideError(true);
            onClearError();
          }}
        >
          {error instanceof Error ? error.message : `Failed to load ${ieKitLabel} data`}
        </Alert>
      )}
      {isLoading && <TableSkeleton aria-label={`Loading ${ieKitLabel} items`} />}
      {!isLoading && !error && (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ flex: 1, minWidth: 220 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  View:
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={view}
                  exclusive
                  onChange={handleViewChange}
                  aria-label={`${ieKitLabel} view`}
                >
                  <ToggleButton value="shortages">Shortages</ToggleButton>
                  <ToggleButton value="overages">Overages</ToggleButton>
                  <ToggleButton value="unauthorized">Unauthorized</ToggleButton>
                  <ToggleButton value="all">All {ieKitLabel} items</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <TextField
                size="small"
                placeholder={`Search by nomenclature, LIN, NSN, or ${ieKitLabel}`}
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: '100%', sm: 240, md: 320 } }}
              />
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewColumnIcon fontSize="small" />}
              onClick={(event) => setColumnSelectorAnchor(event.currentTarget)}
              aria-describedby={selectorId}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
            >
              Manage Columns
            </Button>
          </Stack>
          {!error && isFetched && filteredData.length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {safeData.length === 0
                ? view === 'shortages'
                  ? `No ${ieKitLabel} shortages found for this customer.`
                  : view === 'overages'
                    ? `No ${ieKitLabel} overages found for this customer.`
                    : `No ${ieKitLabel} items found for this customer.`
                : `No ${ieKitLabel} items match your filters.`}
            </Alert>
          )}
          {isFetched && (
            <div ref={gridWrapperRef} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <DataGrid
                density="compact"
                rows={rowsWithId}
                columns={ieColumns}
                disableColumnMenu
                isRowSelectable={() => false}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                autoHeight
                sx={{
                  '& .MuiDataGrid-topContainer': {
                    zIndex: 2,
                    bgcolor: 'background.paper',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />
            </div>
          )}
          <Popover
            id={selectorId}
            open={selectorOpen}
            anchorEl={columnSelectorAnchor}
            onClose={() => setColumnSelectorAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { p: 2, width: 240 } } }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Visible Columns
            </Typography>
            <FormGroup>
              {MANAGEABLE_COLUMNS.map(({ field, label }) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnVisibilityModel[field] ?? true}
                      onChange={handleToggleColumn(field)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </Popover>
        </>
      )}
    </>
  );
};
